import React, { useState, useEffect } from 'react';
import { Landmark, Star } from 'lucide-react';
import api from '../services/api';

interface IncreaseAccount {
  id: string;
  increase_id: string;
  name: string;
  balance_cents: number;
  currency: string;
  status: string;
  is_default: boolean;
}

interface Props {
  value: string;
  onChange: (id: string) => void;
  label?: string;
  className?: string;
  /** If true, auto-select the default account when value is empty */
  autoDefault?: boolean;
}

const BankAccountSelector: React.FC<Props> = ({
  value,
  onChange,
  label = 'Bank Account',
  className = '',
  autoDefault = true,
}) => {
  const [accounts, setAccounts] = useState<IncreaseAccount[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/admin/treasury/accounts') as any;
        const list: IncreaseAccount[] = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) {
          setAccounts(list);
          // auto-select default if nothing is selected
          if (autoDefault && !value && list.length > 0) {
            const def = list.find((a) => a.is_default) || list[0];
            onChange(def.id);
          }
        }
      } catch {
        // silently fail
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className={className}>
        <label className="block text-xs text-muted-foreground mb-1">{label}</label>
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
          <Landmark className="w-4 h-4 animate-pulse" /> Loading accounts…
        </div>
      </div>
    );
  }

  // Nothing to show if the tenant has 0 or 1 accounts
  if (accounts.length <= 1) return null;

  const fmtUSD = (cents: number | string) => {
    const c = typeof cents === 'string' ? parseInt(cents as any, 10) : cents;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((c || 0) / 100);
  };

  return (
    <div className={className}>
      <label className="block text-xs text-muted-foreground mb-1">{label}</label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-gray-900 text-sm"
      >
        <option value="">— Use default account —</option>
        {accounts.map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
            {a.is_default ? ' ★' : ''}
            {' · '}
            {fmtUSD(a.balance_cents)}
            {' · '}
            {a.status}
          </option>
        ))}
      </select>
    </div>
  );
};

export default BankAccountSelector;
